import { useHeaderHeight } from '@react-navigation/elements';
import * as Haptics from 'expo-haptics';
import { ImpactFeedbackStyle } from 'expo-haptics';
import {
  createContext,
  forwardRef,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import {
  KeyboardAvoidingView,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  useWindowDimensions,
  ViewStyle,
} from 'react-native';
import {
  PanGestureHandler,
  PanGestureHandlerGestureEvent,
  TapGestureHandler,
} from 'react-native-gesture-handler';
import Animated, {
  Extrapolate,
  interpolate,
  runOnJS,
  useAnimatedGestureHandler,
  useAnimatedReaction,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import { snapPoint } from 'react-native-redash';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { BehaviorSubject, Subject } from 'rxjs';
import { PanelScrollViewProvider } from '../../appComponents/PanelScrollViewProvider';
import useBehaviorSubject from '../../functions/hooks/useBehaviorSubject';

export const BottomSheetContext = createContext<{
  pos: Animated.SharedValue<number>;
  index: Readonly<Animated.SharedValue<number>>;
  pan: RefObject<PanGestureHandler>;
  panDown: RefObject<PanGestureHandler>;
  isSnapTop$: BehaviorSubject<boolean>;
  isScrollViewOnTop$: BehaviorSubject<boolean>;
  isDragToDismissDisabled: Animated.SharedValue<boolean>;
  height: number;
  snapPoints: Animated.SharedValue<number[]>;
  snapTo: (index: number, callback?: (isFinished: boolean) => void) => void;
  close: () => void;
}>(null as any);

export const BottomSheetProvider = (props: {
  children: ReactNode;
  snapPoints: number[];
  initialIndex?: number;
  snapTo$?: Subject<{
    index: number;
    callback?: (isFinished: boolean) => void;
  }>;
}) => {
  const snapPoints = useSharedValue(props.snapPoints);
  const isScrollViewOnTop$ = useRef(new BehaviorSubject(true)).current;
  const isDragToDismissDisabled = useSharedValue(false);
  const pos = useSharedValue(snapPoints.value[props.initialIndex ?? 0]);
  const isSnapTop$ = useRef(new BehaviorSubject(false)).current;
  const index = useDerivedValue(() => {
    return interpolate(
      pos.value,
      snapPoints.value,
      Array(snapPoints.value.length)
        .fill(null)
        .map((_, i) => i),
      Extrapolate.CLAMP,
    );
  }, [pos, snapPoints]);
  const pan = useRef<PanGestureHandler>(null);
  const panDown = useRef<PanGestureHandler>(null);
  const height = snapPoints.value[snapPoints.value.length - 1];
  const snapTo = useCallback(
    (i: number, callback?: (isFinished: boolean) => void) => {
      if (pos.value === snapPoints.value[i]) {
        callback?.(true);
        return;
      }
      pos.value = withSpring(
        snapPoints.value[i],
        springConfig(0, i === 0),
        isFinished => {
          if (callback != null) {
            runOnJS(callback)(Boolean(isFinished));
          }
        },
      );
      isSnapTop$.next(i === snapPoints.value.length - 1);
    },
    [isSnapTop$, pos, snapPoints.value],
  );
  const close = useCallback(() => snapTo(0), [snapTo]);

  const value = useMemo(
    () => ({
      pos,
      index,
      pan,
      panDown,
      isSnapTop$,
      isScrollViewOnTop$,
      isDragToDismissDisabled,
      height,
      snapPoints,
      snapTo,
      close,
    }),
    [
      close,
      height,
      index,
      isDragToDismissDisabled,
      isScrollViewOnTop$,
      isSnapTop$,
      pos,
      snapPoints,
      snapTo,
    ],
  );

  useEffect(() => {
    if (!props.snapTo$) return;
    const sub = props.snapTo$.subscribe(event => {
      snapTo(event.index, event.callback);
    });
    return () => sub.unsubscribe();
  }, [props.snapTo$, snapTo]);

  return (
    <BottomSheetContext.Provider value={value}>
      {props.children}
    </BottomSheetContext.Provider>
  );
};

const useRealBottomSheetScrollProps = () => {
  const context = useContext(BottomSheetContext);
  const { pan, panDown, isScrollViewOnTop$, isSnapTop$ } = context;
  const isSnapTop = useBehaviorSubject(isSnapTop$)[0];
  const isScrollViewOnTop = useBehaviorSubject(isScrollViewOnTop$)[0];
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      isScrollViewOnTop$.next(event.nativeEvent.contentOffset.y <= 0);
    },
    [isScrollViewOnTop$],
  );
  if (Platform.OS === 'web') {
    return {
      scrollEnabled: isSnapTop,
      scrollEventThrottle: 100,
      onScroll,
    };
  }
  return {
    scrollEventThrottle: 100,
    onScroll,
    waitFor: isScrollViewOnTop ? [pan, panDown] : [],
    keyboardDismissMode: 'on-drag' as const,
  };
};

export const useBottomSheetScrollProps = (): Partial<
  ScrollViewProps & { waitFor: any }
> => {
  const context = useContext(BottomSheetContext);
  if (context == null) {
    return { scrollEventThrottle: 100 };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useRealBottomSheetScrollProps();
};

export function overDragResist(
  between: [number, number],
  pos: number,
  factor = 3,
) {
  // noinspection BadExpressionStatementJS
  'worklet';
  if (pos < between[0]) {
    return between[0] - Math.sqrt(1 + (between[0] - pos)) * factor;
  }
  if (pos > between[1]) {
    return between[1] + Math.sqrt(1 + (pos - between[1])) * factor;
  }
  return pos;
}

function springConfig(velocity: number, isClosing = false) {
  // noinspection BadExpressionStatementJS
  'worklet';
  return {
    velocity,
    damping: 80,
    overshootClamping: isClosing,
    restDisplacementThreshold: isClosing ? 100 : 0.1,
    restSpeedThreshold: isClosing ? 100 : 0.1,
    stiffness: 500,
  };
}

export const BottomSheet = (props: {
  children: ReactNode;
  positioningMethod?: 'translate' | 'position';
  style?: StyleProp<ViewStyle>;
}) => {
  const {
    pan,
    panDown,
    pos,
    isScrollViewOnTop$,
    height,
    snapPoints,
    isDragToDismissDisabled,
    isSnapTop$,
  } = useContext(BottomSheetContext);
  const setIsSnapTop = (input: boolean) => {
    if (isSnapTop$.value == input) {
      return;
    }
    isSnapTop$.next(input);
    if (input) {
      void Haptics.impactAsync(ImpactFeedbackStyle.Light);
    }
  };
  const handler = useAnimatedGestureHandler<
    PanGestureHandlerGestureEvent,
    { init: number }
  >({
    onStart: (_e, context) => {
      context.init = pos.value;
    },
    onActive: (event, context) => {
      const sp = isDragToDismissDisabled.value
        ? snapPoints.value.slice(1)
        : snapPoints.value;
      pos.value = overDragResist(
        [Math.min(...sp), Math.max(...sp)],
        -event.translationY + context.init,
      );
    },
    onEnd: (event, context) => {
      const sp = isDragToDismissDisabled.value
        ? snapPoints.value.slice(1)
        : snapPoints.value;
      const target = snapPoint(
        -event.translationY + context.init,
        -event.velocityY,
        sp,
      );
      const from = pos.value;
      pos.value = withSpring(
        target,
        springConfig(-event.velocityY, target === 0),
        isFinished => {
          runOnJS(setIsSnapTop)((isFinished ? target : from) === height);
        },
      );
      runOnJS(setIsSnapTop)(target === height);
    },
  });
  const style = useAnimatedStyle(() =>
    props.positioningMethod === 'position'
      ? {
          bottom: -(height - pos.value),
        }
      : {
          transform: [{ translateY: height - pos.value }],
        },
  );
  const isSnapTop = useBehaviorSubject(isSnapTop$)[0];
  const isScrollViewOnTop = useBehaviorSubject(isScrollViewOnTop$)[0];
  return (
    <PanGestureHandler
      activeOffsetY={Platform.OS === 'android' ? [-5, 5] : [-1, 1]}
      failOffsetX={Platform.OS === 'android' ? [-10, 10] : [-5, 5]}
      onGestureEvent={handler}
      ref={pan}
      enabled={!isSnapTop}
    >
      <Animated.View
        style={[
          styles.container,
          {
            height: height + 100, // to extend to the bottom
            paddingBottom: 100,
            marginBottom: -100,
          },
          style,
          props.style,
        ]}
      >
        <PanGestureHandler
          onGestureEvent={handler}
          ref={panDown}
          failOffsetY={-10}
          minVelocityY={10}
          enabled={isSnapTop && isScrollViewOnTop}
        >
          <Animated.View style={{ flex: 1 }}>
            <KeyboardAvoidingView
              behavior="padding"
              css={`
                flex: 1;
              `}
            >
              <PanelScrollViewProvider>
                {props.children}
              </PanelScrollViewProvider>
            </KeyboardAvoidingView>
          </Animated.View>
        </PanGestureHandler>
      </Animated.View>
    </PanGestureHandler>
  );
};

const styles = StyleSheet.create({
  container: {
    position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
    borderRadius: 10,
    backgroundColor: 'white',
    overflow: 'hidden',
    left: 0,
    right: 0,
    bottom: 0,
  },
});

export const OnBottomSheetDismiss = (props: { onDismiss: () => void }) => {
  const { pos } = useContext(BottomSheetContext);
  useAnimatedReaction(
    () => pos.value > 0,
    (result, previous) => {
      if (!result && previous) {
        runOnJS(props.onDismiss)();
      }
    },
    [props.onDismiss],
  );
  return null;
};

export const SnapBottomSheet = forwardRef<
  { snapTo: (index: number, callback?: (isFinished: boolean) => void) => void },
  { to: number }
>((props, ref) => {
  const { snapTo } = useContext(BottomSheetContext);
  useImperativeHandle(
    ref,
    () => ({
      snapTo,
    }),
    [snapTo],
  );
  useEffect(() => {
    snapTo(props.to);
  }, [props.to, snapTo]);
  return null;
});

export const BottomSheetBackdrop = (props: {
  style?: StyleProp<ViewStyle>;
  mediumOpacity?: number;
  maxOpacity?: number;
}) => {
  const { mediumOpacity = 0.2, maxOpacity = 0.4 } = props;
  const { index, close, isDragToDismissDisabled } =
    useContext(BottomSheetContext);
  const style = useAnimatedStyle(() => ({
    backgroundColor: 'black',
    opacity: interpolate(
      index.value,
      [0, 1, 2],
      [0, mediumOpacity, maxOpacity],
      Extrapolate.CLAMP,
    ),
  }));
  return (
    <TapGestureHandler
      onActivated={() => {
        if (!isDragToDismissDisabled.value) {
          close();
        }
      }}
    >
      <Animated.View style={[StyleSheet.absoluteFill, style, props.style]} />
    </TapGestureHandler>
  );
};

export const useDisableDragToDismiss = () => {
  const isDragToDismissDisabled =
    useContext(BottomSheetContext)?.isDragToDismissDisabled;
  return useCallback(
    (disabled: boolean) => {
      if (isDragToDismissDisabled != null) {
        isDragToDismissDisabled.value = disabled;
      }
    },
    [isDragToDismissDisabled],
  );
};

export const usePreventDragToDismissWhenMounted = (enabled = true) => {
  const forceKeepOpen = useDisableDragToDismiss();
  useEffect(() => {
    if (!enabled) {
      return;
    }
    forceKeepOpen(true);
    return () => forceKeepOpen(false);
  }, [enabled, forceKeepOpen]);
};

export const MobileBottomPanel = (props: {
  snapPoints?: number[];
  children: ReactNode;
}) => {
  const { height: windowHeight } = useWindowDimensions();
  const headerHeight = useHeaderHeight();
  const { top: safeTop, bottom: safeBottom } = useSafeAreaInsets();
  const minSnapPointTopMargin = Math.max(safeTop, headerHeight);

  return (
    <BottomSheetProvider
      snapPoints={
        props.snapPoints ?? [
          0,
          Math.min(580, windowHeight - minSnapPointTopMargin - 20),
          windowHeight - minSnapPointTopMargin,
        ]
      }
    >
      <BottomSheetBackdrop />
      <BottomSheet>
        <SafeAreaInsetsContext.Provider
          value={{ top: 0, left: 0, right: 0, bottom: safeBottom }}
        >
          {props.children}
        </SafeAreaInsetsContext.Provider>
      </BottomSheet>
    </BottomSheetProvider>
  );
};
